import { Grid, Group } from "@mantine/core"
import { IconPaperclip } from "@tabler/icons-react"
import { Tooltip } from "@tfg/ui"
import i18next from "i18next"
import { useState } from "react"
import { useTranslation } from "react-i18next"

import { LibraryResponses } from "@tfg/types/endpoints/library"

import { DataFetchWrapper } from "~/components/DataFetchWrapper"
import { Property } from "~/components/Property"
import { useFindDocumentUrlQuery } from "~/hooks/useDocumentsQuery"
import { DatapointResponseVersionsModal } from "~/pages/Brand/Products/Details/DatapointResponseVersions/DatapointResponseVersionsModal"
import { DatapointResponseVersionsTrigger } from "~/pages/Brand/Products/Details/DatapointResponseVersions/DatapointResponseVersionsTrigger"
import type { DatapointResponseVersionsModalState } from "~/pages/Brand/Products/Details/DatapointResponseVersions/datapoint-response-versions.types"
import { datapointResponseVersionSnapshotDtoScopeEnum } from "~/gen/api/types/DatapointResponseVersionSnapshotDto.ts"

import { CustomFieldDocumentDrawer } from "./CustomFieldDocumentDrawer"

type CustomFieldProductsDetailsInfoProps = {
  customFieldsDefs: LibraryResponses.ProductDetails["customFieldDefinitions"]
  allCfv: LibraryResponses.CustomFieldValue[]
}

export const CustomFieldProductsDetailsInfo = ({
  customFieldsDefs,
  allCfv,
}: CustomFieldProductsDetailsInfoProps) => {
  const { t } = useTranslation(["products", "common"])
  const [customFieldValue, setCustomFieldValue] = useState<{
    value: string
    label: string
    document: {
      id: string
      originalName: string | null
    } | null
  } | null>(null)
  const [responseVersionsModalState, setResponseVersionsModalState] =
    useState<DatapointResponseVersionsModalState>(null)

  const closeDrawer = () => {
    setCustomFieldValue(null)
  }

  const closeResponseVersionsModal = () => {
    setResponseVersionsModalState(null)
  }

  return (
    <>
      {customFieldsDefs.map((cfd) => {
        const cfv = allCfv.find((cfv) => cfv.customFieldDefinitionId === cfd.id)
        if (!cfd) throw new Error("Custom field definition not found")
        const label =
          cfd.textContent.TextTranslation.find(
            (l) => l.translation === i18next.language.toUpperCase()
          )?.translation || cfd.textContent.originalText

        const document = cfv?.document
        const helpText = document
          ? t("traceability.itemCard.documentName")
          : undefined

        return (
          <div key={`${cfd.id}-${cfv?.id}`}>
            <Grid align="center">
              <Grid.Col span={10}>
                <Property label={label} value={cfv?.value} size="sm" bold />
              </Grid.Col>
              <Grid.Col span={2}>
                <Group gap="xs" wrap="nowrap" align="center">
                  {cfv?.id && (
                    <DatapointResponseVersionsTrigger
                      onClick={() =>
                        setResponseVersionsModalState({
                          fieldLabel: label,
                          target: {
                            scope:
                              datapointResponseVersionSnapshotDtoScopeEnum.CustomFieldValue,
                            customFieldValueId: cfv.id,
                          },
                        })
                      }
                    />
                  )}
                  {document && (
                    <Tooltip label={helpText}>
                      <IconPaperclip
                        size={16}
                        stroke={1.5}
                        cursor="pointer"
                        onClick={() => {
                          setCustomFieldValue({
                            value: cfv?.value || "",
                            label,
                            document: cfv?.document,
                          })
                        }}
                      />
                    </Tooltip>
                  )}
                </Group>
              </Grid.Col>
            </Grid>
          </div>
        )
      })}
      {customFieldValue && (
        <CustomFieldDocumentDrawer
          opened={!!customFieldValue}
          onClose={closeDrawer}
          document={customFieldValue?.document}
          label={customFieldValue?.label}
          cfv={{ value: customFieldValue?.value }}
        />
      )}
      <DatapointResponseVersionsModal
        opened={responseVersionsModalState !== null}
        onClose={closeResponseVersionsModal}
        modalState={responseVersionsModalState}
      />
    </>
  )
}

export const DocumentIFrame = ({
  documentId,
  height = "100%",
  width = "100%",
}: {
  documentId: string
  height?: string
  width?: string
}) => {
  const documentUrlQuery = useFindDocumentUrlQuery(documentId)

  return (
    <DataFetchWrapper query={documentUrlQuery}>
      {(data) => {
        return <iframe src={data.data} height={height} width={width} />
      }}
    </DataFetchWrapper>
  )
}
